/* eslint-disable @typescript-eslint/no-namespace */
/* eslint-disable @typescript-eslint/prefer-namespace-keyword */
import { v4 as uuidv4 } from 'uuid';
import * as dayjs from 'dayjs';
import { EncryptionUtils } from './../encryption/encryption-utils';
import { User } from '../entities/user.entity';
import { SortField } from '../entities/sort-field.entity';
import { SortDirection } from '../enums/sort-direction.enum';
import { RoleLookup } from '../entities/role-lookup.interface';
import { CssRoles } from '../constants/css-roles';
import { Lookup } from '../constants/lookup.interface';
import * as utc from 'dayjs/plugin/utc';
import * as tz from 'dayjs/plugin/timezone';
import { LiveWithTypes } from '../constants/case-management/live-with-types';
import { TrackAttempts } from '../entities/track-attempts.entity';
import { ObjectDifferences } from '../entities/object-differences.interface';
import { LocalStorageKeys } from '../constants/local-storage';

dayjs.extend(tz);
dayjs.extend(utc);

const EdmontonTimezoneCode = 'America/Edmonton';

export function isNullOrUndefined(val) {
  return val === null || val === undefined;
}

export function isNullUndefinedOrEmpty(val: string) {
  return isNullOrUndefined(val) || val === '';
}
export function createChunk(operations: any[], chunkSize) {
  const chunkLength = Math.ceil(operations.length / chunkSize);
  const chunk = [];
  for (let i = 0; i < chunkLength; i += 1) {
    chunk.push(operations.slice(i * chunkSize, (i + 1) * chunkSize));
  }
  return chunk;
}
export function createGuid() {
  return uuidv4();
}
export function createObjectId() {
  const timestamp = ((new Date().getTime() / 1000) | 0).toString(16);
  return (
    timestamp +
    'xxxxxxxxxxxxxxxx'
      .replace(/[x]/g, function () {
        return ((Math.random() * 16) | 0).toString(16);
      })
      .toLowerCase()
  );
}
export function hasValue(item) {
  let hasVal = true;

  if (typeof item === 'undefined') hasVal = false;
  else if (item === null) hasVal = false;
  else if (String(item) === '') hasVal = false;

  return hasVal;
}
export function findObjectByKey(array, key: string, value) {
  if (hasValue(array) && array.length > 0) {
    const item = array.find((i) => i[key] === value);
    if (hasValue(item)) return item;
  }
  return null;
}

export function displayBoolean(value, replacementTrue = 'Yes', replacementFalse = 'No', defaultValueIfNull?) {
  //handles if value is null, show replacement value
  //?? operator returns error in {{ }} brackets
  if (!hasValue(value)) {
    value = false;
    if (hasValue(defaultValueIfNull)) return defaultValueIfNull;
  }
  if (hasValue(replacementTrue) && value === true) return replacementTrue;
  else if (hasValue(replacementFalse) && value === false) return replacementFalse;

  return value;
}

export function convertToDate(value: string): Date {
  //converts string "202002" to date February 2020
  if (value) {
    const year = parseInt(value.substr(0, 4), 10);
    const month = parseInt(value.substr(4, 2), 10) - 1; //month is index based. so february would be 01
    const day = 1;
    const date = new Date(year, month, day);
    return date;
  }
  return null;
}

//all goa-badge that display numbers need to be passed in as strings as the badge component does not allow number types
export function numberToString(count?: number) {
  let countString = '0';
  if (hasValue(count)) {
    countString = count.toString();
  }
  return countString;
}

export function calculateAge(dob: Date, referenceDate?: Date | null) {
  const today = dayjs(referenceDate ?? undefined);
  return today.diff(dob, 'years');
}

export function isBetween14And17(value: Date) {
  if (!value) return false;
  //birthdate should not include hours (which it is). this is causing issues when calculating age in MST evenings if date is exactly 18 years
  //if the date is exactly 18 years, but the 'today' time is less than dob time, it will evaluate to 17 years old
  const safeDate = new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0);
  const age = calculateAge(safeDate);
  return age >= 14 && age <= 17;
}

export function isBetween18And20(value: Date) {
  if (!value) return false;
  //birthdate should not include hours (which it is). this is causing issues when calculating age in MST evenings if date is exactly 18 years
  //if the date is exactly 18 years, but the 'today' time is less than dob time, it will evaluate to 17 years old
  const safeDate = new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0);
  const age = calculateAge(safeDate);
  return age >= 18 && age < 20;
}
export function is20Older(value: Date) {
  return value && calculateAge(value) >= 20;
}

export function is18Older(value?: Date, valueIfNull = false, referenceDate?: Date | null) {
  if (!hasValue(value)) {
    //sometimes we do not want to default/assume someone is under 18
    return valueIfNull;
  }
  return calculateAge(value, referenceDate) >= 18;
}

export function isDependent(clientDependent) {
  return !!(
    clientDependent &&
    clientDependent.dependent &&
    clientDependent.livesWithParent &&
    LiveWithTypes[clientDependent.livesWithParent]?.livesWithParent &&
    clientDependent.dependent.birthdate &&
    (!is18Older(clientDependent.dependent.birthdate) ||
      (isBetween18And20(clientDependent.dependent.birthdate) && clientDependent.dependent.attendingHighSchool))
  );
}

export function isBetween16And18(value: Date) {
  if (!value) return false;
  const safeDate = new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0);
  const age = calculateAge(safeDate);
  return age >= 16 && age <= 18;
}

export function isOver17Years6Mos(value: Date) {
  if (!value) return false;
  const birthDate = dayjs(value);
  const now = dayjs();
  const ageInMonths = now.diff(birthDate, 'month');
  return ageInMonths >= 210;
}

export function omitKeyFromObject(obj, key) {
  //local function to remove key from array (cyclical function call)
  //@typescript-eslint/no-use-before-define lint warning occurs if moved outside of function
  const omitKeyFromArray = function (arr, key) {
    return arr.map((val) => {
      if (Array.isArray(val)) return omitKeyFromArray(val, key);
      else if (typeof val === 'object') return omitKeyFromObject(val, key);
      return val;
    });
  };

  if (obj !== null && obj !== undefined) {
    const keys = Object.keys(obj);
    const newObj = {};
    keys.forEach((i) => {
      if (i !== key) {
        const val = obj[i];
        if (val instanceof Date) newObj[i] = val;
        else if (Array.isArray(val)) newObj[i] = omitKeyFromArray(val, key);
        else if (typeof val === 'object' && val !== null) newObj[i] = omitKeyFromObject(val, key);
        else newObj[i] = val;
      }
    });
    return newObj;
  } else return obj;
}

export function removeTypeNameFromObject<T>(obj: T): T {
  if (!obj) return obj;
  return omitKeyFromObject(obj, '__typename');
}

export function removeTypeNameFromArray<T>(arr: T[]): T[] {
  if (!arr) return null;
  const cleanedArr = [];
  arr.forEach((obj) => cleanedArr.push(omitKeyFromObject(obj, '__typename')));
  // for (let i = 0; i < arr.length; i++) {
  //   arr[i] = omitKeyFromObject(arr[i], '__typename');
  // }
  return cleanedArr;
}

export function removeDeletedFromSubCollections<T>(object: T): T {
  const deleteRemoved = Object.entries(object).reduce(
    (patchedObject, [key, value]) => ({
      ...patchedObject,
      [key]:
        Array.isArray(value) && value.find((o) => o.isDeleted != undefined) ? value.filter((o) => !o.isDeleted) : value,
    }),
    {} as T
  );

  return deleteRemoved;
}

export function getKey(obj) {
  return new Proxy(obj, {
    get(_, key) {
      return key;
    },
  });
}

export function unsort() {
  return 0;
}

export function getSessionStorageArray<Type>(key: string, encryptionKey = null) {
  const sessionValue = this.getSessionStorageValue(key, encryptionKey);
  if (sessionValue) {
    const storedArray = !Array.isArray(sessionValue) ? (JSON.parse(sessionValue) as Array<Type>) : sessionValue; //no brackets
    return storedArray;
  }
  return [];
}

export function addSessionStorageArray<Type>(key: string, value: Type, clear?: boolean, encryptionKey = null) {
  let sessionValue = this.getSessionStorageValue(key, encryptionKey);
  if (clear !== null && clear) sessionValue = []; //clear arrays
  else if (sessionValue !== null) {
    const arrayTest = JSON.parse(sessionValue.toString());
    if (!Array.isArray(arrayTest)) sessionValue = []; //existing data is not an array. clear
  }
  if (!sessionValue) {
    const arrayValue = [value];
    const stringArrayValue = JSON.stringify(arrayValue);
    this.setSessionStorageValue(key, stringArrayValue, encryptionKey);
  } else {
    //push to array
    const storedArray = !Array.isArray(sessionValue) ? (JSON.parse(sessionValue) as Array<Type>) : sessionValue;
    storedArray.push(value);
    const stringArrayValue = JSON.stringify(storedArray);
    this.setSessionStorageValue(key, stringArrayValue, encryptionKey);
  }
}

export function setSessionStorageValue<Type>(key: string, value: Type, encryptionKey: string = null) {
  let storeValue = null;
  if (encryptionKey) {
    const encUtils = new EncryptionUtils(encryptionKey);
    const encryptValue = encUtils.encrypt(JSON.stringify(value));
    storeValue = encryptValue;
  } else if (value !== null) storeValue = JSON.stringify(value);

  window.sessionStorage.setItem(key, storeValue);
}

export function getSessionStorageValue(key: string, encryptionKey = null) {
  const sessionStorageValue = window.sessionStorage.getItem(key);
  let objValue = null;

  if (sessionStorageValue) {
    if (encryptionKey) {
      const encUtils = new EncryptionUtils(encryptionKey);
      const decryptValue = encUtils.decrypt(sessionStorageValue);
      if (decryptValue) objValue = JSON.parse(decryptValue);
    } else if (sessionStorageValue !== null) {
      objValue = JSON.parse(sessionStorageValue);
    }
  }

  return objValue;
}

export function getLocalStorageValue(key: string, encryptionKey = null) {
  const localStorageValue = window.localStorage.getItem(key);
  let objValue = null;
  if (encryptionKey) {
    const encUtils = new EncryptionUtils(encryptionKey);
    const decryptValue = encUtils.decrypt(localStorageValue);
    if (decryptValue) objValue = JSON.parse(decryptValue);
  } else if (localStorageValue !== null) {
    objValue = JSON.parse(localStorageValue);
  }
  return objValue;
}

export function setLocalStorageValue<Type>(key: string, value: Type, encryptionKey: string = null) {
  let storeValue = null;
  if (encryptionKey) {
    const encUtils = new EncryptionUtils(encryptionKey);
    const encryptValue = encUtils.encrypt(JSON.stringify(value));
    storeValue = encryptValue;
  } else if (value !== null) storeValue = JSON.stringify(value);

  window.localStorage.setItem(key, storeValue);
}

export function removeLocalStorageValue(key: string) {
  window.localStorage.removeItem(key);
}

export function removeSessionStorageValue(key: string) {
  window.sessionStorage.removeItem(key);
}

export function flattenObject(obj) {
  let flattened = {};

  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object' && !(obj[key] instanceof Date) && obj[key] !== null)
      flattened = { ...flattened, ...flattenObject(obj[key]) };
    else flattened[key] = obj[key];
  });

  return flattened;
}

export function randomBetween(min, max) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

export function mapUserToDto(user: User) {
  // keep null/undefined by returning the blank user passed in
  return user
    ? {
        _id: user._id,
        name: user.name,
        email: user.email,
        primaryRole: user.primaryRole,
        location: user.location,
        district: user.district,
        serviceProviderId: user.serviceProviderId,
      }
    : user;
}

function getPrimaryRole(userRoleNames: string[]) {
  if (!userRoleNames) return null;
  const userRoles = CssRoles.AllValues.filter((r) => userRoleNames.includes(r.code));
  const userRolesByPriority = userRoles.sort((a, b) => a.priority - b.priority);
  return userRolesByPriority[0];
}

export function mapKeycloakUserToUser(user) {
  if (!user) return;

  return {
    _id: user.userid,
    name: user.name,
    email: user.email,
    phone: user.phone,
    district: user.district,
    roles: user.roles || user.realm_access?.roles,
    primaryRole: getPrimaryRole(user.roles || user.realm_access?.roles)?.displayValue ?? user.defaultRole,
    location: user.groups?.length ? user.groups[0]?.replace('/', '') : '', //user.location,
  } as User;
}

export function objectContains(obj, searchTerm: string): boolean {
  for (const key in obj) {
    if (obj[key]) {
      if (obj[key] instanceof Date) {
        if (obj[key].toString().includes(searchTerm)) return true;
      } else if (obj[key] instanceof Object) {
        const matchFound = objectContains(obj[key], searchTerm);
        if (matchFound) return true;
      } else {
        const matchFound = obj[key].toString().indexOf(searchTerm) !== -1;
        if (matchFound) return true;
      }
    }
  }
  return false;
}

export function hrTimeToNanoseconds(hrTime) {
  //hrTime is [seconds, nanoseconds]
  const nanoseconds = hrTime[0] * 1000000000 + hrTime[1];
  return nanoseconds;
}

export function hrTimeToMicroseconds(hrTime) {
  //hrTime is [seconds, nanoseconds]
  //return hrTime[0] * 1000000 + hrTime[1] / 1000;
  const nanoseconds = this.hrTimeToNanoseconds(hrTime);
  return nanoseconds / 1000;
}

export function hrTimeToMilliseconds(hrTime) {
  //hrTime is [seconds, nanoseconds]
  const nanoseconds = this.hrTimeToMicroseconds(hrTime);
  return nanoseconds / 1000;
}

/**
 * Returns true if user is in roles
 */
export function isInRoles(user: User, roles: RoleLookup[]) {
  if (!user || !(user.roles || user.primaryRole) || !roles) return false;
  if (user.primaryRole) {
    const inRole = roles.some((r) => r.displayValue === user.primaryRole);
    if (inRole) return true;
  }
  const approvedRoles = roles.map((r) => r.code);
  return user.roles?.filter((userRole) => approvedRoles.includes(userRole)).length > 0;
}

/**
 * Gets the value of the specified property in the object provided.
 * Use dot notation for nested fields.
 *
 * Note: This function calls itself recursively chaining down each field in
 * a dot notation formatted fieldName
 *
 * @param obj
 *    The object to get the field of
 * @param fieldName
 *    The name of the field to retrieve.  Use dot notation for nested fields
 * @returns
 *    The value of the property specified
 */
export function getField(obj: unknown, fieldName: string) {
  const fields = fieldName.split('.');
  if (fields.length === 1) return obj[fields[0]];

  let currentFieldName = fields[0];
  let field = obj[currentFieldName];
  let openBracketIdx = currentFieldName.indexOf('[');
  while (openBracketIdx !== -1) {
    //if the open bracket is NOT at the start of the string,
    //then there is a field name to grab, not just the indexer
    if (openBracketIdx > 0) {
      const namePart = currentFieldName.substring(0, openBracketIdx);
      currentFieldName = currentFieldName.substring(openBracketIdx);
      field = obj[namePart];
    }

    const closeBracketIdx = currentFieldName.indexOf(']');
    const indexer = currentFieldName.substring(0, closeBracketIdx);
    currentFieldName = currentFieldName.substring(closeBracketIdx);

    const idx = parseInt(indexer.replace('[', '').replace(']', ''));
    field = field[idx];

    openBracketIdx = currentFieldName.indexOf('[');
  }

  return getField(field, fields.slice(1).join('.'));
}

/**
 * Sorts an array of any type of object by the field names and sort directions provided.
 * Use dot notation to specify sorts on nested properties.  Objects are sorted in the order
 * the sort fields are provided.
 * 
 * @param sortFields 
 *    array of the properties to sort by
 *    if a property you want to sort by is a nested property, use dot notation
 *    i.e. 
      const sortFields = [
        { fieldName: 'createdBy.name', direction: SortDirection.Asc} as SortField,
        { fieldName: 'createdAt', direction: SortDirection.Desc} as SortField,
      ];
 * @returns 
 */
export function sortBy(sortFields: SortField[]) {
  return (a, b) => {
    let result;

    //the OR functionality will check each sortField in order passed in
    sortFields.forEach((sortField) => {
      let x, y;
      if (sortField.direction === SortDirection.Asc) {
        x = getField(a, sortField.fieldName);
        y = getField(b, sortField.fieldName);
      } else {
        x = getField(b, sortField.fieldName);
        y = getField(a, sortField.fieldName);
      }

      if (x instanceof Date) result = result || x.getTime() - y.getTime();
      else if (typeof x === 'string') result = result || x.localeCompare(y);
      else result = result || x - y;
    });

    return result;
  };
}
export function getMoreRecentUpdatedAt(prev?: Date, curr?: { updatedAt?: Date }) {
  return ((!prev || (curr?.updatedAt && curr.updatedAt.getTime() > prev.getTime())) && curr?.updatedAt) || prev;
}

export function getTypeName(val) {
  if (val instanceof Date) return 'Date';
  else return typeof val;
}

export function namedLookupToArray(namedLookup: { [name: string]: Lookup }) {
  return (Object.values(namedLookup) as Lookup[]).map((r) => ({ value: r.code, displayValue: r.displayValue }));
}

/**
 * Transpose the provided date to UTC timezone and format using the provided template.
 * @param date Date to be formatted.  Now when undefined.
 * @param format Date format template. ISO8601 when undefined.
 * @returns Formatted date.
 */
export function utcTime(date?: Date, format?: string): string {
  return dayjs(date).utc().format(format);
}

/**
 * Transpose the provided date to 'America/Edmonton' (MST or MDT) timezone and format using the provided template.
 * @param date Date to be formatted. Now when undefined.
 * @param format Date format template.  ISO8601 when undefined.
 * @returns Formatted date.
 */
export function edmontonTime(date?: Date, format?: string): string {
  return dayjs(date).tz(EdmontonTimezoneCode).format(format);
}
// same as new Date(dobValue + ' 00:00') or dayjs(date).
// dayjs(date).tz(EdmontonTimezoneCode) and dayjs.tz(date, EdmontonTimezoneCode) are different
export function toEdmontonTime(date) {
  return dayjs(date).tz(EdmontonTimezoneCode);
}
export function newDateWithEdmontonTime(dateTimeString: string) {
  return dayjs.tz(dateTimeString, EdmontonTimezoneCode).toDate();
}
export function getEdmontonLocalDate() {
  return dayjs().tz(EdmontonTimezoneCode).startOf('day').toDate();
}

//converts string to ordinal. Example "January 1, 2026 to "January 1st, 2026"
export function formatLongDateWithOrdinal(dateStr: string): string | undefined {
  if (!dateStr) return;
  const cleaned = dateStr.replace(',', '').trim();
  const parts = cleaned.split(' ');
  if (parts.length < 2) return;
  const monthName = parts[0];
  const day = parseInt(parts[1], 10);
  const year = parts[2] ?? new Date().getFullYear().toString();
  if (isNaN(day)) return;
  const getOrdinal = (n: number): string => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  return `${monthName} ${getOrdinal(day)}, ${year}`;
}

export function getPermutations(n, elements) {
  let results = [];
  if (n == 1) results.push(elements.join(' '));
  else {
    let recursiveVal;
    for (let i = 0; i < n - 1; i++) {
      recursiveVal = this.getPermutations(n - 1, elements);
      results = [...results, ...recursiveVal];
      if (n % 2 == 0) this.swapElementsInArray(elements, i, n - 1);
      else this.swapElementsInArray(elements, 0, n - 1);
    }
    recursiveVal = this.getPermutations(n - 1, elements);
    results = [...results, ...recursiveVal];
  }

  return results;
}

export function swapElementsInArray(input, a, b) {
  const tmp = input[a];
  input[a] = input[b];
  input[b] = tmp;
}

export function regExpEscape(s: string) {
  return s.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
}

export function MapMaritalSatus(maritalStatus: string) {
  return !maritalStatus || maritalStatus === 'Unknown'
    ? null
    : maritalStatus === 'Common Law'
    ? 'commonLaw'
    : maritalStatus.toLowerCase();
}

export function MapGender(gender: string) {
  return !gender || gender === 'Unknown' ? null : gender === 'X' ? 'preferNotToSay' : gender.toLowerCase();
}

export function wildcardToRegExp(s: string) {
  if (s?.trim()) return new RegExp(s.split(/\*+/).map(regExpEscape).join('.*'), 'ims');
  return new RegExp('.*', 'ims');
}

export function getNameVariants(arr: string[]) {
  const results = [];
  for (let i = 0; i <= arr.length; i++) {
    const firstName = arr.slice(0, i).join(' ');
    const lastName = arr.slice(i).join(' ');

    results.push({ firstName: firstName, lastName: lastName });
  }

  return results;
}

export function createCursorCriteria(sortCriteria, cursor) {
  if (!cursor) return {};

  const orList = [];
  const runningMatch = {};
  for (const [key, value] of Object.entries(sortCriteria)) {
    const orVal = { ...runningMatch };
    const cursorEntry = cursor.find((c) => c.key === key);
    let cursorVal;
    switch (cursorEntry.type) {
      case 'Date':
        cursorVal = new Date(+cursorEntry.value);
        break;
      case 'number':
        cursorVal = +cursorEntry.value;
        break;
      case 'boolean':
        cursorVal = cursorEntry.value === 'true';
        break;
      default:
        cursorVal = cursorEntry.value;
        break;
    }

    if (cursorVal === null) orVal[key] = { $ne: cursorVal };
    else if (value === SortDirection.Asc) orVal[key] = { $gt: cursorVal };
    else orVal[key] = { $lt: cursorVal };

    runningMatch[key] = cursorVal;

    orList.push(orVal);
  }

  return { $or: orList };
}

export function createNextCursor(sortCriteria, cursorData) {
  if (!cursorData) return [];

  const nextCursor = [];
  for (const key of Object.keys(sortCriteria)) {
    const keySplit = key.split('.');
    let data = cursorData;
    keySplit.forEach((k) => (data = data[k]));

    nextCursor.push({ key: key, value: data, type: this.getTypeName(data) });
  }

  return nextCursor;
}

function isIso8601(value): boolean {
  const ISO_8601 = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/;
  if (value === null || value === undefined) return false;
  return ISO_8601.test(value);
}

export function deserializeToDates(object) {
  if (object === null || object === undefined) return object;
  if (typeof object !== 'object') return object;

  for (const key of Object.keys(object)) {
    const value = object[key];
    if (isIso8601(value)) object[key] = new Date(value);
    else if (typeof value === 'object') deserializeToDates(value);
  }
}

export function getMonthNumber(monthName: string): number {
  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

  const monthAbrvLower = monthName.toLowerCase().substring(0, 3);

  const idx = months.findIndex((m) => m === monthAbrvLower);

  //convert from 0 based to 1 based
  return idx + 1;
}

export function groupBy(objectArray, property) {
  return objectArray.reduce(function (acc, obj) {
    const key = obj[property];
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});
}

export function replaceAll(originalString: string, findText: string, replaceText: string) {
  let newString = originalString;
  if (originalString?.indexOf(findText) > -1) {
    //need to do a replaceAll
    newString = originalString.split(findText).join(replaceText);
  }
  return newString;
}

//updated deepCopy to preserve dates and keep them Dates instead of string when serialized
export function deepCopy<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj;

  if (obj instanceof Date) return new Date(obj.getTime()) as any;

  if (Array.isArray(obj)) return obj.map(deepCopy) as any;

  const clonedObj: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      clonedObj[key] = deepCopy((obj as any)[key]);
    }
  }

  return clonedObj;
}

export function getClientName(client: {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  preferredName?: string; //will prioritize preferred name as first name if value is passed in
}) {
  const firstName = hasValue(client?.preferredName) ? client?.preferredName : client?.firstName;

  if (!client || (!firstName && !client?.middleName && !client?.lastName)) return '';
  const fullName =
    (client.middleName && `${firstName ?? ''} ${client?.middleName ?? ''} ${client?.lastName ?? ''}`) ||
    `${firstName ?? ''} ${client?.lastName ?? ''}`;
  return fullName?.trim();
}

export function getTitle(title: string, suffix: string, prefix: string = null): string {
  if (title) {
    if (prefix) {
      return `${prefix} - ${title} - ${suffix}`;
    } else {
      return `${title} - ${suffix}`;
    }
  }
  return '';
}

//This function will compare 2 objects (of any type) and detect if there are any changes, additions, or removals regardless of order.
//Optional: 'excludedAttributes' you can pass in nodes/attributes to exclude (comma separated string)
//Optional: 'excludeFields' to filter out changes from final result set if there are false positives.
/*Example:
  const obj1 = { name: "Alice", age: 25, city: "New York", documentList: { active: false } };
  const obj2 = { name: "Alice", age: 25, city: "San Francisco", documentList: null };
  const excludedAttributes = "age"; 
  const excludeFields = "documentList.active";
  const differences = compareObjects(obj1, obj2, excludedAttributes, excludeFields);
  */
export function compareObjects<T = unknown>(
  object1: T,
  object2: T,
  excludedAttributes = '',
  excludeFields = ''
): ObjectDifferences<T> {
  const jsonStr1 = JSON.stringify(object1);
  const jsonStr2 = JSON.stringify(object2);

  const excludedSet = new Set(excludedAttributes.split(',').map((prop) => prop.trim()));

  function cleanObject(obj: unknown): unknown {
    if (Array.isArray(obj)) {
      return obj.map(cleanObject).filter((item) => item !== null && item !== undefined);
    } else if (obj && typeof obj === 'object') {
      return Object.keys(obj as Record<string, unknown>).reduce((acc: Record<string, unknown>, key) => {
        const value = cleanObject((obj as Record<string, unknown>)[key]);
        if (value !== null && value !== undefined && !excludedSet.has(key)) {
          acc[key] = value;
        }
        return acc;
      }, {});
    }
    return obj;
  }

  function findDifferences(o1: unknown, o2: unknown, differences: ObjectDifferences<T>, path = ''): void {
    if (Array.isArray(o1) && Array.isArray(o2)) {
      if (o1.length !== o2.length) {
        differences.changed[path] = { before: o1 as T, after: o2 as T };
      } else {
        o1.forEach((item, index) => {
          findDifferences(item, o2[index], differences, `${path}[${index}]`);
        });
      }
    } else if (o1 && typeof o1 === 'object' && o2 && typeof o2 === 'object') {
      const obj1 = o1 as Record<string, unknown>;
      const obj2 = o2 as Record<string, unknown>;

      const keys1 = new Set(Object.keys(obj1));
      const keys2 = new Set(Object.keys(obj2));

      for (const key of keys1) {
        const newPath = path ? `${path}.${key}` : key;
        if (!keys2.has(key)) {
          differences.removed[newPath] = obj1[key] as T;
        } else {
          findDifferences(obj1[key], obj2[key], differences, newPath);
        }
      }

      for (const key of keys2) {
        const newPath = path ? `${path}.${key}` : key;
        if (!keys1.has(key)) {
          differences.added[newPath] = obj2[key] as T;
        }
      }
    } else if (o1 !== o2) {
      differences.changed[path] = { before: o1 as T, after: o2 as T };
    }
  }

  function filterEntries(entries: ObjectDifferences<T>, excludeFields: string): ObjectDifferences<T> {
    if (!excludeFields) {
      return entries;
    }

    const excludeFieldsSet = new Set(excludeFields.split(',').map((field) => field.trim()));

    function filterObject<U>(obj: Record<string, U>): Record<string, U> {
      return Object.fromEntries(Object.entries(obj).filter(([key]) => !excludeFieldsSet.has(key)));
    }

    return {
      added: filterObject(entries.added),
      removed: filterObject(entries.removed),
      changed: filterObject(entries.changed),
      hasChanges: entries.hasChanges,
    };
  }

  try {
    const obj1 = cleanObject(JSON.parse(jsonStr1));
    const obj2 = cleanObject(JSON.parse(jsonStr2));

    const differences: ObjectDifferences<T> = {
      hasChanges: false,
      added: {},
      removed: {},
      changed: {},
    };

    findDifferences(obj1, obj2, differences);

    const results = filterEntries(differences, excludeFields);

    results.hasChanges =
      Object.keys(results.added).length > 0 ||
      Object.keys(results.removed).length > 0 ||
      Object.keys(results.changed).length > 0;

    return results;
  } catch (error: any) {
    throw new Error(`Invalid JSON input: ${error.message}`);
  }
}

//For verified users they cannot change their first, middle, or last name.
//If Alberta.ca returns us leading special characters or "." as name fields, it violates our name validator and prevents user the ability to submit
//Solution: CEC's manually fix these by removing leading special characters. If firstName is invalid, it gets replaced with lastName
//applicant object format: { firstName: string, lastName: string, middleName: string}. Will return same object with transformed name
export function specialCharacterNameTransform<T>(applicant: unknown): T {
  let containsLeadingSpecialCharacters = false;
  const lastNameValid = applicant['lastName']?.length > 0 && !this.hasLeadingSpecialCharacters(applicant['lastName']);

  //check first name and if invalid then replace it with last name
  containsLeadingSpecialCharacters = this.hasLeadingSpecialCharacters(applicant['firstName']);
  if (containsLeadingSpecialCharacters) {
    const originalFirstName = applicant['firstName'];
    applicant['firstName'] = applicant['firstName'].replace(/^[^a-zA-Z0-9]+/, '').trim(); //replace all leading special characters
    if (!applicant['firstName'] && lastNameValid) {
      applicant['firstName'] = applicant['lastName'];
    } else if (!applicant['firstName']) {
      applicant['firstName'] = originalFirstName; //cannot replace firstname with invalid last
    }
  }

  //check middle name and if invalid set to blank
  containsLeadingSpecialCharacters = this.hasLeadingSpecialCharacters(applicant['middleName']);
  if (containsLeadingSpecialCharacters) {
    applicant['middleName'] = applicant['middleName'].replace(/^[^a-zA-Z0-9]+/, '').trim(); //replace all leading special characters
  }

  return applicant as T;
}

export function hasLeadingSpecialCharacters(value: string) {
  if (!value) {
    return false;
  }
  const hasLeadingSpecialCharacter = /^[^a-zA-Z0-9]/.test(value);
  // console.log(hasLeadingSpecialCharacter); // true
  return hasLeadingSpecialCharacter;
}

//allows us to be able to get nested properties (ex. 'application.client.firstName)
export function getNestedProperty<T>(obj: any, path: string): T | undefined {
  if (!path) return obj as T; // Return the entire object if path is empty
  return path.split('.').reduce((acc, key) => acc && acc[key], obj);
}

//allows us to set nest properties
export function setNestedProperty(obj: any, path: string, value: any): void {
  const keys = path.split('.');
  let current = obj;

  keys.slice(0, -1).forEach((key, index) => {
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {}; // Ensure each level exists
    }
    current = current[key];
  });

  current[keys[keys.length - 1]] = value;
}

/**
 * Calculates the last day of the fiscal year for a given date.
 * Fiscal year runs from April 1 to March 31.
 *
 * Examples:
 * - Jan 15, 2025 -> Mar 31, 2025 (fiscal year 2025)
 * - Mar 31, 2025 -> Mar 31, 2025 (fiscal year 2025)
 * - Apr 1, 2025 -> Mar 31, 2026 (fiscal year 2026)
 * - Dec 31, 2025 -> Mar 31, 2026 (fiscal year 2026)
 *
 * @param date Optional date to calculate from. Defaults to current date.
 * @returns The last day of the fiscal year (March 31)
 */

export function getLastDayOfFiscalYear(date?: Date): Date {
  const now = date || new Date();
  const month = now.getMonth(); // 0-11 (0=Jan, 2=Mar, 3=Apr)

  // If Jan-Mar (months 0-2), fiscal year ends Mar 31 of current year
  // If Apr-Dec (months 3-11), fiscal year ends Mar 31 of next year
  const year = month < 3 ? now.getFullYear() : now.getFullYear() + 1;

  return new Date(year, 2, 31); // Month 2 = March, day 31
}

/**
 * Calculates the first day of the fiscal year for a given date.
 * Fiscal year runs from April 1 to March 31.
 *
 * Examples:
 * - Jan 15, 2025 -> Apr 1, 2024 (fiscal year 2025)
 * - Mar 31, 2025 -> Apr 1, 2024 (fiscal year 2025)
 * - Apr 1, 2025 -> Apr 1, 2025 (fiscal year 2026)
 * - Dec 31, 2025 -> Apr 1, 2025 (fiscal year 2026)
 *
 * @param date Optional date to calculate from. Defaults to current date.
 * @returns The first day of the fiscal year (April 1)
 */
export function getFiscalYearStart(date?: Date): Date {
  const now = date || new Date();
  const month = now.getMonth(); // 0-11 (0=Jan, 2=Mar, 3=Apr)

  // If Jan-Mar (months 0-2), fiscal year starts Apr 1 of previous year
  // If Apr-Dec (months 3-11), fiscal year starts Apr 1 of current year
  const year = month < 3 ? now.getFullYear() - 1 : now.getFullYear();

  return new Date(year, 3, 1); // Month 3 = April, day 1
}

/**
 * Checks if a given date falls within the same fiscal year as the reference date.
 * Fiscal year runs from April 1 to March 31.
 *
 * @param date The date to check
 * @param fiscalDate Optional reference date to calculate the fiscal year from. Defaults to current date.
 * @returns True if the date is within the same fiscal year as the reference date
 */
export function isInFiscalYear(date: Date, fiscalDate?: Date): boolean {
  const referenceDate = fiscalDate || new Date();
  const fyStart = getFiscalYearStart(referenceDate);
  const fyEnd = getLastDayOfFiscalYear(referenceDate);

  return date >= fyStart && date <= fyEnd;
}
export function fillTemplate(template: string, data: Record<string, string>): string {
  return template.replace(/<<(\w+)>>/g, (_, key) => data[key] || '');
}
//pass in full name (with spaces)
//will return first letter capitalized
export function capitalizeName(name: string): string {
  if (!hasValue(name)) {
    return null;
  }
  return name
    .trim() // remove leading/trailing spaces
    .toLowerCase()
    .split(/\s+/) // split on one or more whitespace characters
    .map((word) => word.replace(/(^|[-'])([a-z])/g, (_, separator, letter) => `${separator}${letter.toUpperCase()}`))
    .join(' ');
}

//allows us to throttle a method to prevent it from being called more than a specified amount of time
//usage example: Utilities.throttle(this.refreshSession.bind(this), 5000); // 5000ms = 5s
export function throttle(fn: (...args: any[]) => void, limit: number) {
  let inThrottle = false;
  return function (this: any, ...args: any[]) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

//central spot to determine if current environemtn is PROD based on environment name
export function isProdEnvironment(environmentName?: string) {
  const envName = environmentName?.trim()?.toLowerCase();
  const isProd = envName && (envName === 'production' || envName === 'prod');
  return isProd;
}

/**
 * Returns the number of days passed since the given date.
 * @param {Date} date - The date to compare (e.g. job.data.submittedDate)
 * @returns {number} Days passed since the given date
 */
export function daysSince(date: Date) {
  return dayjs().diff(dayjs(date), 'day');
}

//TERENCE: there's a defect with design systems component that it's not displaying version number
/*  
  Hack to ensure version number is displayed in the micro header
  <goab-microsite-header type="beta" version="1.0.0"   
*/
//TODO: remove this once it has been fixed
export function ensureVersionNumber(versionNumber: string): void {
  // Grab the microsite header
  const header = document.querySelector('goab-microsite-header');
  if (!header) return;

  // Find the slot="version" div
  const versionDiv = header.querySelector('div[slot="version"]');
  if (!versionDiv) return;

  // Trim text + check if there are child nodes
  const hasContent =
    (versionDiv.textContent && versionDiv.textContent.trim().length > 0) || versionDiv.children.length > 0;

  // If empty → inject version
  if (!hasContent) {
    versionDiv.textContent = versionNumber;
  }
}

//evaluates the track attempts and will reset lockout if one of these conditions are met:
// 1. reset date is greater than last attempt date
// 2. if lockout window has expired
// 3. lastAttempt occurred more than 10 mins ago (lockoutLengthMinutes).
export function getTrackAttempts(trackAttempts?: TrackAttempts, resetDate: Date = null, lockoutLengthMinutes = 10) {
  const dateNow = new Date();
  let resetAttempts = false;
  if (!trackAttempts) {
    trackAttempts = { failedAttempts: 0 } as TrackAttempts;
  }
  if (trackAttempts.lastAttempt) {
    //check if attempts should be reset
    const lastAttemptDate = new Date(trackAttempts.lastAttempt);
    const timeoutDateFalloff = new Date(lastAttemptDate.getTime() + lockoutLengthMinutes * 60 * 1000);
    if (dateNow > timeoutDateFalloff) {
      //last attempt expired, reset values
      resetAttempts = true;
    }
  }
  if (
    !resetAttempts &&
    ((resetDate !== null && trackAttempts.lastAttempt < resetDate) ||
      (trackAttempts.lockedOutUntil && trackAttempts.lockedOutUntil < dateNow))
  ) {
    //resetDate resets locked out OR locked out window expired
    resetAttempts = true;
  }

  if (resetAttempts) {
    trackAttempts.failedAttempts = 0;
    trackAttempts.lockedOutUntil = null;
  }
  return trackAttempts;
}

//evaluates when invalid attempt occurs and will trigger lockout window if it exceeds maxFailedAttemps for the length of lockoutLengthMinutes
export function incrementTrackAttempts(
  trackAttempts?: TrackAttempts,
  lockoutLengthMinutes = 10,
  maxFailedAttempts = 5
) {
  const dateNow = new Date();
  if (!trackAttempts) {
    trackAttempts = { failedAttempts: 0 } as TrackAttempts;
  } else if (!trackAttempts?.failedAttempts) {
    trackAttempts.failedAttempts = 0;
  }

  if (trackAttempts.lockedOutUntil && trackAttempts.lockedOutUntil > dateNow) {
    // console.log('do nothing');
    //locked out, do nothing
    return trackAttempts;
  } else {
    // console.log('increase failed attempts');
    //not locked out, increment failed attempts and lock out if nessessary
    trackAttempts.failedAttempts++;
    trackAttempts.lastAttempt = dateNow;
    if (trackAttempts.failedAttempts >= maxFailedAttempts) {
      trackAttempts.lockedOutUntil = new Date(Date.now() + lockoutLengthMinutes * 60 * 1000);
    }
    return trackAttempts;
  }
}

//Evaluates TrackAttempts to determine if current state is inside of a lockout. If you just pass in a lockedOutUntil date, it will only evaluate that.
// 1. Checks lockedOutUntil and compares it with now.
// 2. Lockout reset if resetDate is passed in, then will evaluate.
// 3. Also resets based on date/time fall off (lastAttempt + lockoutLengthMinutes) < now
export function isLockedOutTrackAttempts(
  trackAttempts?: TrackAttempts,
  resetDate?: Date,
  lockoutLengthMinutes?: number
) {
  const updatedAttempts = getTrackAttempts(trackAttempts, resetDate, lockoutLengthMinutes);
  if (!updatedAttempts?.lockedOutUntil) {
    return false;
  }
  const now = new Date();
  if (updatedAttempts?.lockedOutUntil > now) {
    return true;
  }
  return false;
}

// returns only specified properties of an object
// const user = { name: 'Terence', 'phone': '000-000-0000'};
// const result = selectFields(user, ['name']);
// // { name: 'Terence' }
export function selectFields<T extends object, K extends keyof T>(
  obj: T | null | undefined,
  keys?: K[]
): Pick<T, K> | null {
  if (!obj || !keys || keys.length === 0) {
    return null;
  }
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  const returnObject = Object.keys(result).length > 0 ? result : null;
  return returnObject;
}

//returns true if the provided version is greater than or equal to the minimum version
//example: versionCompatible('4.8.19', '4.8.18') => true
export function versionCompatible(version?: string, minVersion?: string): boolean {
  if (!minVersion) return true; //if no value for minVersion then we consider all versions compatible
  if (!version) return false; //if application has no version then it is not compatible with any minimum version.
  const toNumbers = (v: string) => v.split('.').map((n) => parseInt(n, 10));
  const current = toNumbers(version);
  const minimum = toNumbers(minVersion);
  const length = Math.max(current.length, minimum.length);
  for (let i = 0; i < length; i++) {
    const cur = current[i] || 0;
    const min = minimum[i] || 0;

    if (cur > min) return true;
    if (cur < min) return false;
  }
  return true; // versions are equal
}

//allows us to deep replace multiple strings within multiple properties in an object recursively
// Example:
// const data = { groupName: "Janet's info", nested: { title: "Janet's docs" } };
// const result = deepReplaceMulti(data, [
//   { search: "Janet", replace: "Jane", keys: ['groupName', 'title'] }
// ]);
// result --> { groupName: "Jane's info", nested: { title: "Jane's docs" } }
export function deepReplace(obj: any, replacements: { search: string; replace: string; keys: string[] }[]): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => this.deepReplace(item, replacements));
  }

  // Handle Plain Strings
  if (typeof obj === 'string') {
    let value = obj;
    for (const r of replacements) {
      if (r.keys.length === 0 || r.keys.includes('')) {
        value = value.replace(new RegExp(r.search, 'g'), r.replace);
      }
    }
    return value;
  }

  if (obj !== null && typeof obj === 'object') {
    const newObj: any = {};
    for (const key in obj) {
      let value = obj[key];
      if (typeof value === 'string') {
        for (const r of replacements) {
          if (r.keys.includes(key)) {
            value = value.replace(new RegExp(r.search, 'g'), r.replace);
          }
        }
        newObj[key] = value;
      } else {
        newObj[key] = this.deepReplace(value, replacements);
      }
    }
    return newObj;
  }
  return obj;
}

//Not sure if this is the best way to access version number from package.json, but using this for now.
//version number always appears in the meta version tag on the header
export function getCurrentSiteVersion() {
  let version = document.querySelector('meta[name="version"]')?.getAttribute('content') ?? null;
  if (!hasValue(version)) {
    version = getLocalStorageValue(LocalStorageKeys.ApplicationVersion.key);
  }
  return version;
}
